import React from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Slide,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import { Close, CheckCircle, Error, Warning, Info } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

export type NotificationSeverity = 'success' | 'error' | 'warning' | 'info';

export interface NotificationMessage {
  id: string;
  severity: NotificationSeverity;
  title?: string;
  message: string;
  duration?: number;
}

interface NotificationSnackbarProps {
  notification: NotificationMessage | null;
  onClose: () => void;
}

const getIcon = (severity: NotificationSeverity) => {
  const icons = {
    success: <CheckCircle />,
    error: <Error />,
    warning: <Warning />,
    info: <Info />,
  };
  return icons[severity];
};

const NotificationSnackbar: React.FC<NotificationSnackbarProps> = ({ notification, onClose }) => {
  const theme = useTheme();

  if (!notification) return null;

  const getSeverityColor = (severity: NotificationSeverity) => {
    const colors = {
      success: theme.palette.success.main,
      error: theme.palette.error.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main,
    };
    return colors[severity];
  };

  return (
    <AnimatePresence>
      {notification && (
        <Snackbar
          open={true}
          autoHideDuration={notification.duration || 6000}
          onClose={onClose}
          TransitionComponent={Slide}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Alert
              severity={notification.severity}
              onClose={onClose}
              variant="filled"
              icon={getIcon(notification.severity)}
              action={
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={onClose}
                >
                  <Close fontSize="small" />
                </IconButton>
              }
              sx={{
                minWidth: 300,
                boxShadow: `0 8px 32px ${alpha(getSeverityColor(notification.severity), 0.25)}`,
                backgroundColor: notification.severity === 'success' 
                  ? '#4caf50' // 明確な緑色に変更
                  : getSeverityColor(notification.severity),
                '& .MuiAlert-icon': {
                  fontSize: 28,
                },
                '& .MuiAlert-message': {
                  fontSize: '0.95rem',
                },
                borderRadius: 2,
              }}
            >
              {notification.title && (
                <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
                  {notification.title}
                </AlertTitle>
              )}
              {notification.message}
            </Alert>
          </motion.div>
        </Snackbar>
      )}
    </AnimatePresence>
  );
};

export default NotificationSnackbar;